/* ************************************************************************** */
/*                                Dependencies                                */
/* ************************************************************************** */

// Lib dependencies
import { useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { showNotification } from '@mantine/notifications';

// UI lib components
import { Stack, Container, ScrollArea, Alert } from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import QuestionGroupPicker from '@features/Support/QuestionGroupPicker';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Hooks
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Assets
import { IconAlertCircle as IconEmptyGroupsAlert } from '@tabler/icons-react';

// Local API & Types
import {
  useListGroupsQuery,
  useUpdateQuestionGroupsMutation,
} from '@app-types/blackroom-api';
import { DialogQuestionGroupProps } from './types';
import { RowGroup } from '@app/types/blackroom-app';

/* ************************************************************************** */
/*                                  Component                                 */
/* ************************************************************************** */

function DialogQuestionGroup({
  questionIds,
  isSelectAll,
  scope,
}: DialogQuestionGroupProps) {
  /* ********************************** HOOKS ********************************* */

  // State
  const [selectedGroups, setSelectedGroups] = useState<RowGroup[]>([]);

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { closeDialog } = dialogStore ?? {};
  const workspace = useWorkspace();

  // API
  const { data: groupsData } = useListGroupsQuery({
    variables: { filter: { workspaceId: workspace.id }, limit: 100 },
    fetchPolicy: 'network-only',
  });
  const [updateQuestionGroups] = useUpdateQuestionGroupsMutation();

  const allGroups = groupsData?.groups.nodes;

  const refetchQuestions = useRefetchQuestions();

  // Localization
  const { t } = useTranslation(['Common', 'Support']);

  /* ******************************** CALLBACKS ******************************* */

  const handleToggleGroup = (
    targetGroups: { group: RowGroup; selected: boolean }[],
  ) => {
    targetGroups.forEach((targetGroup) => {
      if (!targetGroup.selected) {
        setSelectedGroups((prevGroups) =>
          prevGroups.filter((group) => group.id !== targetGroup.group.id),
        );
        return;
      }

      setSelectedGroups((prevGroups) => [...prevGroups, targetGroup.group]);
    });
  };

  async function onSubmit() {
    try {
      closeDialog?.('dialog-support-question-group');
      await updateQuestionGroups({
        variables: {
          updateQuestionGroupsInput: {
            groupIds: selectedGroups.map((group) => group.id),
            questionIds,
            shouldRetainOldGroups: false,
            workspaceId: workspace.id,
            scope,
            isSelectAll,
          },
        },
      });
      refetchQuestions();
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Common:errors.genericMessage'),
        color: 'blackroomRed',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Container
      w="30vw"
      onKeyDown={function cb(e) {
        switch (e.key) {
          case 'Enter':
            onSubmit();
            break;
          default:
            break;
        }
      }}
      fluid
    >
      <Stack align="stretch" h="100%">
        <form onSubmit={(e) => e.preventDefault()}>
          {allGroups && (
            <ScrollArea mah={300} style={{ overflow: 'auto' }}>
              <QuestionGroupPicker
                groups={allGroups}
                selectedGroups={selectedGroups}
                allGroupsSelected={selectedGroups.length === allGroups.length}
                onToggleGroup={handleToggleGroup}
              />
            </ScrollArea>
          )}
        </form>

        {selectedGroups.length === 0 && (
          <Alert
            variant="light"
            color="blackroomRed"
            title={t(
              'Support:PageSupport.supportActions.dialogQuestionGroup.alert.title',
            )}
            icon={<IconEmptyGroupsAlert />}
            p="xs"
          >
            {t(
              'Support:PageSupport.supportActions.dialogQuestionGroup.alert.text',
            )}
          </Alert>
        )}

        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.save'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={onSubmit}
          onCancel={() => {
            dialogStore?.closeDialog?.('dialog-support-question-group');
          }}
        />
      </Stack>
    </Container>
  );
}

export default DialogQuestionGroup;
